import { useMemo } from "react";
import type { GroupSessionDetail } from "@/pages/app/provider/scheduling/group-appointment";
import { useEncounterSummeriesV2 } from "@/queries/encounter/use-encounter-summery-v2";
import { useGroupSessionById } from "@/queries/group-visit/use-group-session-by-id";
import { useGroupVisitById } from "@/queries/group-visit/use-group-visit-by-id";
import { getXtenantId } from "@/utils/utils";
import {
  SavedEncounterNote,
  SessionDocumentationHeader,
  SessionParticipant,
} from "../group-visit-types";
import {
  isSessionDocumentationLocked,
  toSessionDocumentationHeader,
  toSessionParticipant,
} from "../group-visit-utils";

export type UseSessionDocumentation = {
  session?: GroupSessionDetail;
  header: SessionDocumentationHeader;
  /** Every enrolled patient with the state of their visit note. */
  roster: SessionParticipant[];
  /** Completed or cancelled: nothing can be documented any more. */
  isLocked: boolean;
  isLoading: boolean;
  isNotFound: boolean;
};

/**
 * The session being documented — header, roster and each patient's note state.
 * Note states come from the latest encounter summary of each appointment, so
 * they survive a refresh.
 *
 * @author Rajkumar Rathod
 */
export const useSessionDocumentation = (
  sessionId?: string
): UseSessionDocumentation => {
  const {
    data: session,
    isLoading: isSessionLoading,
    isError,
  } = useGroupSessionById(sessionId);

  // The session carries no description of its own; it comes from the group.
  const { data: group } = useGroupVisitById(session?.groupVisitId);

  const appointmentIds = useMemo(
    () => (session?.patients ?? []).map((patient) => patient.appointmentId),
    [session]
  );
  const summaries = useEncounterSummeriesV2(appointmentIds, getXtenantId());
  const isRosterLoading = summaries.some((query) => query.isLoading);

  const isLocked = isSessionDocumentationLocked(session?.sessionStatus);

  const roster = useMemo<SessionParticipant[]>(
    () =>
      (session?.patients ?? []).map((patient, index) =>
        toSessionParticipant(
          patient,
          summaries[index]?.data?.data as SavedEncounterNote | undefined,
          isLocked
        )
      ),
    // Each query object is recreated per render; the data is what matters.

    [session, isLocked, ...summaries.map((query) => query.data)]
  );

  const header = useMemo<SessionDocumentationHeader>(
    () => (session ? toSessionDocumentationHeader(session, group) : {}),
    [session, group]
  );

  return {
    session,
    header,
    roster,
    isLocked,
    isLoading: isSessionLoading || isRosterLoading,
    isNotFound: !sessionId || isError || (!isSessionLoading && !session),
  };
};
